import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Folder,
  FileText,
  ChevronRight,
  FolderPlus,
  Upload,
  Download,
  Trash2,
  MoreVertical,
  Pencil,
  Loader2,
  FolderInput,
  GripVertical,
  FolderOpen,
} from "lucide-react";
import { documentsApi, foldersApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import CreateFolderModal from "../documents/CreateFolderModal";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FolderData {
  _id: string;
  name: string;
  parentId?: string | null;
  color?: string;
  itemCount?: number;
  createdAt: string;
}

interface DocumentData {
  _id: string;
  title: string;
  type: string;
  folderId?: string | null;
  currentVersionId?: {
    _id: string;
    file?: { filename: string; mimeType: string; sizeBytes: number };
    storage?: { key: string };
  } | null;
  createdAt: string;
}

interface DragItem {
  id: string;
  name: string;
  kind: "folder" | "document";
}

interface MoveIntent {
  item: DragItem;
  targetFolderId: string | null;
  targetFolderName: string;
}

interface Props {
  matterId: string;
  clientId?: string | null;
  matterTitle?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Component ────────────────────────────────────────────────────────────────

const MatterDocumentsTab: React.FC<Props> = ({ matterId, clientId, matterTitle }) => {
  // Navigation
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([]);

  // Content
  const [subfolders, setSubfolders] = useState<FolderData[]>([]);
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);

  // Drag-drop
  const [dragItem, setDragItem] = useState<DragItem | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [dragOverBreadcrumb, setDragOverBreadcrumb] = useState<number | null>(null);

  // Move modal
  const [moveIntent, setMoveIntent] = useState<MoveIntent | null>(null);
  const [moving, setMoving] = useState(false);

  // Folder management
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<FolderData | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);

  // File management
  const [deleteDocTarget, setDeleteDocTarget] = useState<DocumentData | null>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Folder context menu
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // ─── Data Fetching ────────────────────────────────────────────────────────

  const fetchContents = useCallback(async () => {
    setLoading(true);
    try {
      if (currentFolderId) {
        // Inside a folder — use getContents for one round-trip
        const res = await foldersApi.getContents(currentFolderId);
        const data = res.data.data || res.data;
        setSubfolders(data.subfolders || []);
        setDocuments(data.documents || []);
      } else {
        // Root level — fetch folders + root-only docs
        const [foldersRes, docsRes] = await Promise.all([
          foldersApi.list({ matterId, parentId: "root" }),
          documentsApi.list({ matterId, folderId: "root", limit: 200 }),
        ]);
        setSubfolders(foldersRes.data.data || foldersRes.data || []);
        setDocuments(docsRes.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, matterId]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  // Close context menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setFolderMenuId(null);
      }
    };
    if (folderMenuId) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [folderMenuId]);

  // ─── Navigation ───────────────────────────────────────────────────────────

  const navigateToFolder = (folder: FolderData) => {
    setFolderPath((prev) => [...prev, { id: folder._id, name: folder.name }]);
    setCurrentFolderId(folder._id);
    setFolderMenuId(null);
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setFolderPath([]);
      setCurrentFolderId(null);
    } else {
      const target = folderPath[index];
      setFolderPath((prev) => prev.slice(0, index + 1));
      setCurrentFolderId(target.id);
    }
  };

  // ─── Drag-Drop Handlers ──────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, item: DragItem) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  const handleDragOverFolder = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    // Don't highlight if dragging a folder onto itself
    if (dragItem?.kind === "folder" && dragItem.id === folderId) return;
    setDragOverFolderId(folderId);
  };

  const handleDragOverBreadcrumb = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverBreadcrumb(index);
  };

  const handleDragLeaveFolder = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (e.currentTarget.contains(related)) return;
    setDragOverFolderId(null);
  };

  const handleDragLeaveBreadcrumb = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (e.currentTarget.contains(related)) return;
    setDragOverBreadcrumb(null);
  };

  const handleDropOnFolder = (e: React.DragEvent, targetFolder: FolderData) => {
    e.preventDefault();
    setDragOverFolderId(null);
    setDragOverBreadcrumb(null);
    if (!dragItem) return;
    if (dragItem.kind === "folder" && dragItem.id === targetFolder._id) return;

    setMoveIntent({
      item: dragItem,
      targetFolderId: targetFolder._id,
      targetFolderName: targetFolder.name,
    });
  };

  const handleDropOnBreadcrumb = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverFolderId(null);
    setDragOverBreadcrumb(null);
    if (!dragItem) return;

    const targetFolderId = index < 0 ? null : folderPath[index].id;
    const targetFolderName = index < 0 ? (matterTitle || "Documents") : folderPath[index].name;

    // Don't drop into current folder
    if (targetFolderId === currentFolderId) return;

    setMoveIntent({
      item: dragItem,
      targetFolderId,
      targetFolderName,
    });
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverFolderId(null);
    setDragOverBreadcrumb(null);
  };

  // ─── Move Execution ───────────────────────────────────────────────────────

  const executeMove = async () => {
    if (!moveIntent) return;
    setMoving(true);
    try {
      if (moveIntent.item.kind === "folder") {
        await foldersApi.update(moveIntent.item.id, { parentId: moveIntent.targetFolderId || null });
      } else {
        await documentsApi.update(moveIntent.item.id, { folderId: moveIntent.targetFolderId || null });
      }
      setMoveIntent(null);
      fetchContents();
    } catch (err) {
      console.error("Move failed:", err);
    } finally {
      setMoving(false);
    }
  };

  // ─── Folder Actions ───────────────────────────────────────────────────────

  const handleRenameSubmit = async (folderId: string) => {
    if (!renameValue.trim()) return;
    try {
      await foldersApi.update(folderId, { name: renameValue.trim() });
      setRenamingFolder(null);
      setRenameValue("");
      fetchContents();
    } catch (err) {
      console.error("Rename failed:", err);
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    setDeletingFolder(true);
    try {
      await foldersApi.remove(deleteFolderTarget._id);
      setDeleteFolderTarget(null);
      fetchContents();
    } catch (err) {
      console.error("Delete folder failed:", err);
    } finally {
      setDeletingFolder(false);
    }
  };

  // ─── File Actions ─────────────────────────────────────────────────────────

  const handleUpload = async (file: File) => {
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", file.name);
      formData.append("matterId", matterId);
      if (currentFolderId) formData.append("folderId", currentFolderId);
      if (clientId) formData.append("clientId", clientId);
      await documentsApi.upload(formData);
      fetchContents();
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDoc = async () => {
    if (!deleteDocTarget) return;
    setDeletingDoc(true);
    try {
      await documentsApi.remove(deleteDocTarget._id);
      setDeleteDocTarget(null);
      fetchContents();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeletingDoc(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const isEmpty = subfolders.length === 0 && documents.length === 0;

  return (
    <div className="space-y-4">
      {/* Toolbar Row */}
      <div className="flex items-center justify-between gap-3">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-1.5 text-sm min-w-0 flex-1">
          <Folder className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <button
            onClick={() => navigateToBreadcrumb(-1)}
            onDragOver={(e) => handleDragOverBreadcrumb(e, -1)}
            onDragLeave={handleDragLeaveBreadcrumb}
            onDrop={(e) => handleDropOnBreadcrumb(e, -1)}
            className={`font-medium px-1.5 py-0.5 rounded-md transition-all ${
              dragOverBreadcrumb === -1
                ? "bg-emerald-100 text-emerald-700 scale-105"
                : folderPath.length === 0
                ? "text-slate-800"
                : "text-slate-500 hover:text-emerald-700"
            }`}
          >
            {matterTitle || "Documents"}
          </button>
          {folderPath.map((crumb, idx) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
              <button
                onClick={() => navigateToBreadcrumb(idx)}
                onDragOver={(e) => handleDragOverBreadcrumb(e, idx)}
                onDragLeave={handleDragLeaveBreadcrumb}
                onDrop={(e) => handleDropOnBreadcrumb(e, idx)}
                className={`font-medium px-1.5 py-0.5 rounded-md truncate transition-all ${
                  dragOverBreadcrumb === idx
                    ? "bg-emerald-100 text-emerald-700 scale-105"
                    : idx === folderPath.length - 1
                    ? "text-slate-800"
                    : "text-slate-500 hover:text-emerald-700"
                }`}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowCreateFolder(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
          >
            <FolderPlus className="h-4 w-4" />
            New Folder
          </button>
          <label
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white rounded-xl transition-colors cursor-pointer"
            style={{ backgroundColor: uploadingDoc ? "var(--color-hover)" : "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (!uploadingDoc) (e.currentTarget as HTMLElement).style.backgroundColor = "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              if (!uploadingDoc) (e.currentTarget as HTMLElement).style.backgroundColor = "var(--color-primary)";
            }}
          >
            {uploadingDoc ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploadingDoc ? "Uploading..." : "Upload"}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              disabled={uploadingDoc}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : isEmpty ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center"
        >
          <FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">
            {currentFolderId ? "This folder is empty" : "No documents yet"}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Upload files or create folders to organize your matter documents
          </p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          {/* Table Header */}
          <div className="grid grid-cols-[auto_1fr_100px_120px_80px] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="w-6" />
            <div>Name</div>
            <div className="hidden md:block">Size</div>
            <div className="hidden md:block">Modified</div>
            <div className="text-right">Actions</div>
          </div>

          {/* Folder Rows */}
          {subfolders.map((folder) => (
              <div
                key={`folder-${folder._id}`}
                draggable
                onDragStart={(e) =>
                  handleDragStart(e, {
                    id: folder._id,
                    name: folder.name,
                    kind: "folder",
                  })
                }
                onDragOver={(e) => handleDragOverFolder(e, folder._id)}
                onDragLeave={(e) => handleDragLeaveFolder(e)}
                onDrop={(e) => handleDropOnFolder(e, folder)}
                onDragEnd={handleDragEnd}
                className={`grid grid-cols-[auto_1fr_100px_120px_80px] gap-2 items-center px-4 py-2.5 border-b border-slate-100 last:border-b-0 transition-all cursor-grab active:cursor-grabbing group ${
                  dragOverFolderId === folder._id
                    ? "ring-2 ring-inset ring-emerald-500 bg-emerald-50 scale-[1.01]"
                    : dragItem?.id === folder._id
                    ? "opacity-40"
                    : "hover:bg-slate-50"
                }`}
              >
                {/* Drag handle */}
                <div className="w-6 flex items-center justify-center">
                  <GripVertical className="h-4 w-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Name */}
                <div
                  className="flex items-center gap-2 min-w-0 cursor-pointer"
                  onClick={() => {
                    if (renamingFolder !== folder._id) navigateToFolder(folder);
                  }}
                >
                  <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  {renamingFolder === folder._id ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRenameSubmit(folder._id);
                        if (e.key === "Escape") {
                          setRenamingFolder(null);
                          setRenameValue("");
                        }
                      }}
                      onBlur={() => handleRenameSubmit(folder._id)}
                      className="text-sm font-medium text-slate-800 border border-emerald-300 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 w-full max-w-[200px]"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="text-sm font-medium text-slate-800 truncate hover:text-emerald-700 transition-colors">
                      {folder.name}
                    </span>
                  )}
                </div>

                {/* Size — folders show item count or dash */}
                <div className="text-sm text-slate-400 hidden md:block">
                  {folder.itemCount != null ? `${folder.itemCount} items` : "—"}
                </div>

                {/* Date */}
                <div className="text-sm text-slate-400 hidden md:block">
                  {formatDate(folder.createdAt)}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end relative" ref={folderMenuId === folder._id ? menuRef : undefined}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMenuId(folderMenuId === folder._id ? null : folder._id);
                    }}
                    className="p-1 rounded-lg hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <MoreVertical className="h-4 w-4 text-slate-400" />
                  </button>

                  {folderMenuId === folder._id && (
                    <div className="absolute right-0 top-8 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-20 w-36">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenamingFolder(folder._id);
                          setRenameValue(folder.name);
                          setFolderMenuId(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Rename
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteFolderTarget(folder);
                          setFolderMenuId(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Document Rows */}
            {documents.map((doc) => {
              const version = doc.currentVersionId;
              const storageKey = version?.storage?.key;
              const fileName = version?.file?.filename || doc.title || "Untitled";
              const fileSize = version?.file?.sizeBytes;

              return (
                <div
                  key={`doc-${doc._id}`}
                  draggable
                  onDragStart={(e) =>
                    handleDragStart(e, {
                      id: doc._id,
                      name: doc.title || fileName,
                      kind: "document",
                    })
                  }
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-[auto_1fr_100px_120px_80px] gap-2 items-center px-4 py-2.5 border-b border-slate-100 last:border-b-0 transition-all cursor-grab active:cursor-grabbing group ${
                    dragItem?.id === doc._id ? "opacity-40" : "hover:bg-slate-50"
                  }`}
                >
                  {/* Drag handle */}
                  <div className="w-6 flex items-center justify-center">
                    <GripVertical className="h-4 w-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Name */}
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-800 truncate">
                      {doc.title || fileName}
                    </span>
                  </div>

                  {/* Size */}
                  <div className="text-sm text-slate-400 hidden md:block">
                    {fileSize ? formatBytes(fileSize) : "—"}
                  </div>

                  {/* Date */}
                  <div className="text-sm text-slate-400 hidden md:block">
                    {formatDate(doc.createdAt)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-1">
                    {storageKey && (
                      <a
                        href={documentsApi.downloadUrl(storageKey)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100"
                        title="Download"
                      >
                        <Download className="h-4 w-4 text-slate-500" />
                      </a>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteDocTarget(doc);
                      }}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
        </motion.div>
      )}

      {/* ─── Modals ────────────────────────────────────────────────────────── */}

      {/* Create Folder Modal */}
      <CreateFolderModal
        open={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        onCreated={() => {
          setShowCreateFolder(false);
          fetchContents();
        }}
        parentId={currentFolderId || undefined}
        clientId={clientId || undefined}
        matterId={matterId}
      />

      {/* Move Confirmation Modal */}
      <PremiumModal
        open={!!moveIntent}
        onClose={() => setMoveIntent(null)}
        title="Move Item"
        icon={FolderInput}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setMoveIntent(null)}
              disabled={moving}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={executeMove}
              disabled={moving}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => {
                if (!moving) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) => {
                if (!moving) e.currentTarget.style.backgroundColor = "var(--color-primary)";
              }}
            >
              {moving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Moving...
                </>
              ) : (
                <>
                  <FolderInput className="h-4 w-4" />
                  Move
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            Move <span className="font-semibold">"{moveIntent?.item.name}"</span> into{" "}
            <span className="font-semibold">"{moveIntent?.targetFolderName}"</span>?
          </p>
          <p className="text-xs text-slate-400">
            This can be undone by dragging the item back.
          </p>
        </div>
      </PremiumModal>

      {/* Delete Folder Confirmation Modal */}
      <PremiumModal
        open={!!deleteFolderTarget}
        onClose={() => setDeleteFolderTarget(null)}
        title="Delete Folder"
        icon={Trash2}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setDeleteFolderTarget(null)}
              disabled={deletingFolder}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteFolder}
              disabled={deletingFolder}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deletingFolder ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            Delete <span className="font-semibold">"{deleteFolderTarget?.name}"</span>?
          </p>
          <p className="text-xs text-slate-400">
            Documents inside will be moved to the parent folder.
          </p>
        </div>
      </PremiumModal>

      {/* Delete Document Confirmation Modal */}
      <PremiumModal
        open={!!deleteDocTarget}
        onClose={() => setDeleteDocTarget(null)}
        title="Delete Document"
        icon={Trash2}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setDeleteDocTarget(null)}
              disabled={deletingDoc}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteDoc}
              disabled={deletingDoc}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deletingDoc ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </>
              )}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          Delete <span className="font-semibold">"{deleteDocTarget?.title}"</span>? This action cannot be undone.
        </p>
      </PremiumModal>
    </div>
  );
};

export default MatterDocumentsTab;
